import React from 'react';

const EMAIL = "dandarhealth@outlook.com"

export const PrivacyPolicyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 via-white to-blue-50">
      <div className="max-w-4xl mx-auto px-6 py-16 sm:py-24">
        {/* Header section with improved styling */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold mb-6 text-gray-900 tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-blue-800">
            Privacy Policy
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Your privacy is our priority. Learn how we protect and manage your information.
          </p>
        </div>

        {/* Introduction section with enhanced design */}
        <section className="mb-12 bg-white rounded-3xl p-8 sm:p-10 shadow-lg border border-gray-100 transform transition-all hover:shadow-xl">
          <h2 className="text-2xl font-semibold mb-6 text-gray-800 flex items-center">
            <span className="bg-blue-100 p-2 rounded-lg mr-4">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </span>
            Introduction
          </h2>
          <p className="mb-4 text-gray-700">
            At Dandar Health, we are committed to protecting your privacy and ensuring the security of your personal information.
            This Privacy Policy outlines how we collect, use, disclose, and safeguard your information when you use our platform
            and services.
          </p>
          <p className="mb-4 text-gray-700">
            By using Dandar Health's services, you consent to the data practices described in this policy.
          </p>
        </section>

        {/* Information Collection section with improved layout */}
        <section className="mb-12 bg-white rounded-3xl p-8 sm:p-10 shadow-lg border border-gray-100">
          <h2 className="text-2xl font-semibold mb-6 text-gray-800 flex items-center">
            <span className="bg-blue-100 p-2 rounded-lg mr-4">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </span>
            Information We Collect
          </h2>
          <ul className="grid gap-4 mb-4">
            {[
              { title: 'Personal Information', desc: 'Name, email address, phone number, and other contact details.', icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' },
              { title: 'Health Information', desc: 'Medical history, diagnoses, treatments, medications, and other health-related data.', icon: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z' },
              { title: 'Account Information', desc: 'Login credentials, user preferences, and profile information.', icon: 'M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z' },
              { title: 'Usage Data', desc: 'Information about how you interact with our platform, including access times, pages viewed, and features used.', icon: 'M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
              { title: 'Device Information', desc: 'IP address, browser type, device type, and operating system.', icon: 'M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' }
            ].map((item, index) => (
              <li key={index} className="flex items-start p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                <span className="flex-shrink-0 p-2 bg-blue-100 rounded-lg mr-4">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                  </svg>
                </span>
                <div>
                  <h3 className="font-medium text-gray-900 mb-1">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </div>
              </li>
            ))}
          </ul>
        </section>

        {/* How We Use Information section with card design */}
        <section className="mb-12 bg-white rounded-3xl p-8 sm:p-10 shadow-lg border border-gray-100">
          <h2 className="text-2xl font-semibold mb-6 text-gray-800 flex items-center">
            <span className="bg-blue-100 p-2 rounded-lg mr-4">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </span>
            How We Use Your Information
          </h2>
          <p className="mb-4 text-gray-700">
            We use the collected information for various purposes, including:
          </p>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              'Providing and maintaining our healthcare services',
              'Processing and facilitating appointments',
              'Improving user experience and platform functionality',
              'Communicating with you about your care and our services',
              'Complying with legal obligations and healthcare regulations',
              'Analyzing usage patterns to enhance our platform'
            ].map((item, index) => (
              <li key={index} className="flex items-center bg-gray-50 rounded-lg p-4">
                <svg className="w-5 h-5 text-blue-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-700">{item}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Data Security section */}
        <section className="mb-12 bg-white rounded-3xl p-8 sm:p-10 shadow-lg border border-gray-100">
          <h2 className="text-2xl font-semibold mb-6 text-gray-800 flex items-center">
            <span className="bg-blue-100 p-2 rounded-lg mr-4">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </span>
            Data Security
          </h2>
          <div className="bg-gray-50 rounded-xl p-6 mb-4">
            <p className="text-gray-700 leading-relaxed">
              We implement strict security measures to protect your personal information from unauthorized access,
              alteration, disclosure, or destruction. These measures include:
            </p>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              {[
                'End-to-end encryption',
                'Secure access controls',
                'Regular security assessments',
                'Healthcare compliance standards'
              ].map((item, index) => (
                <li key={index} className="flex items-center bg-white rounded-lg p-4 shadow-sm">
                  <svg className="w-5 h-5 text-green-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Sharing Information section */}
        <section className="mb-12 bg-white rounded-3xl p-8 sm:p-10 shadow-lg border border-gray-100">
          <h2 className="text-2xl font-semibold mb-6 text-gray-800 flex items-center">
            <span className="bg-blue-100 p-2 rounded-lg mr-4">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </span>
            Sharing Your Information
          </h2>
          <p className="text-gray-700 mb-6">
            We may share your information with:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                title: 'Healthcare Providers',
                desc: 'Doctors, clinics, and other medical professionals involved in your care.',
                icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10'
              },
              {
                title: 'Service Providers',
                desc: 'Third-party vendors who help us operate our platform and provide services.',
                icon: 'M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z'
              },
              {
                title: 'Legal Entities',
                desc: 'When required by law, court order, or government regulation.',
                icon: 'M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3'
              }
            ].map((item, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-6 hover:bg-gray-100 transition-colors">
                <div className="flex items-center mb-4">
                  <span className="p-2 bg-blue-100 rounded-lg mr-3">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                    </svg>
                  </span>
                  <h3 className="font-semibold text-gray-900">{item.title}</h3>
                </div>
                <p className="text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-6 p-4 bg-yellow-50 rounded-xl border border-yellow-100">
            <p className="text-yellow-800 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              We do not sell or rent your personal information to third parties for marketing purposes.
            </p>
          </div>
        </section>

        {/* Privacy Rights section */}
        <section className="mb-12 bg-white rounded-3xl p-8 sm:p-10 shadow-lg border border-gray-100">
          <h2 className="text-2xl font-semibold mb-6 text-gray-800 flex items-center">
            <span className="bg-blue-100 p-2 rounded-lg mr-4">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </span>
            Your Privacy Rights
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              'Access to your personal data',
              'Correction of inaccurate information',
              'Deletion of your data',
              'Restriction of processing',
              'Data portability',
              'Objection to processing'
            ].map((right, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-4 hover:bg-gray-100 transition-colors">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-blue-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-700">{right}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6 p-6 bg-blue-50 rounded-xl">
            <p className="text-gray-700">
              To exercise these rights, please contact us using the information provided at the end of this policy.
            </p>
          </div>
        </section>

        {/* Policy Changes section */}
        <section className="mb-12 bg-white rounded-3xl p-8 sm:p-10 shadow-lg border border-gray-100">
          <h2 className="text-2xl font-semibold mb-6 text-gray-800 flex items-center">
            <span className="bg-blue-100 p-2 rounded-lg mr-4">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </span>
            Changes to This Policy
          </h2>
          <div className="bg-gray-50 rounded-xl p-6">
            <p className="text-gray-700 mb-4">
              We may update our Privacy Policy from time to time. We will notify you of any changes by:
            </p>
            <ul className="space-y-3">
              {[
                'Posting the new Privacy Policy on this page',
                'Updating the "Last Updated" date',
                'Sending an email notification for significant changes',
                'Displaying a prominent notice on our platform'
              ].map((item, index) => (
                <li key={index} className="flex items-center bg-white rounded-lg p-4 shadow-sm">
                  <svg className="w-5 h-5 text-blue-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Contact section with enhanced visual appeal */}
        <section className="bg-white rounded-3xl p-8 sm:p-10 shadow-lg border border-gray-100">
          <h2 className="text-2xl font-semibold mb-6 text-gray-800 flex items-center">
            <span className="bg-blue-100 p-2 rounded-lg mr-4">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </span>
            Contact Us
          </h2>
          <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-6 flex flex-col sm:flex-row items-center justify-between">
            <div className="text-gray-700 mb-4 sm:mb-0">
              <p className="text-lg font-medium mb-2">Have questions about our privacy policy?</p>
              <p className="text-gray-600">We're here to help. Reach out to us at:</p>
            </div>
            <a
              href={`mailto:${EMAIL}`}
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              {EMAIL}
            </a>
          </div>
          {/* Last updated date */}
          <p className="text-gray-600 mt-4 text-sm">Last updated: 03/04/2025</p>
        </section>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage; 